import { useMutation, useQuery } from '@tanstack/react-query'
import { useMatch, useNavigate, useParams, useSearch } from '@tanstack/react-router'
import { toast } from 'sonner'

import { useBooleanState } from '@/lib/hooks/use-boolean-state'
import { submitOCRTasksMutation } from '@/lib/queries/business/v1/ocr/submit/mutations'
import { type ReqSubmitOCRTasksMutation } from '@/lib/queries/business/v1/ocr/submit/types'
import { documentOCRListQuery } from '@/lib/queries/business/v1/ocr/task/queries'

import { useDocumentOCRResultStore } from '../../store'
import { constructOCRDataPayload } from '../../utils'

export const useProcessDocumentTopBar = () => {
  const navigate = useNavigate()

  const { jobId } = useParams({ strict: false })
  const { taskId: taskIdSearchParam } = useSearch({ strict: false })

  const isInReviewPage = useMatch({ from: '/process-document/review/$jobId', shouldThrow: false })

  const { data: documentOCRListData } = useQuery(documentOCRListQuery(jobId ? parseInt(jobId) : undefined))
  const { ocrResults, clear: clearDocumentOCRResultStore } = useDocumentOCRResultStore()

  const { mutateAsync: submitTasks, isPending: isSubmittingOCRTasks } = useMutation(submitOCRTasksMutation)

  const {
    booleanState: isOpenLeavePageConfirmDialog,
    setToTrue: openLeavePageConfirmDialog,
    setToFalse: closeLeavePageConfirmDialog,
  } = useBooleanState()

  const currentTaskItemIndex = documentOCRListData?.findIndex(({ taskId }) => taskId === taskIdSearchParam)
  const taskIds = documentOCRListData?.map(({ taskId }) => taskId)
  const isFirstTaskItem = currentTaskItemIndex === 0
  const isLastTaskItem = documentOCRListData ? currentTaskItemIndex === documentOCRListData.length - 1 : false
  const isAllTaskReviewed = documentOCRListData?.every(({ isReviewed }) => isReviewed)

  const handleClickBackToHomeButton = () => {
    openLeavePageConfirmDialog()
  }

  const handleChangeOpenLeavePageConfirmDialog = (open: boolean) => {
    open ? openLeavePageConfirmDialog() : closeLeavePageConfirmDialog()
  }

  const handleClickLeavePageConfirmDialogCancelButton = () => {
    closeLeavePageConfirmDialog()
  }

  const handleClickLeavePageConfirmDialogOkButton = () => {
    void navigate({ to: '/upload-document' })
    clearDocumentOCRResultStore()
  }

  const handleClickPreviousTaskButton = () => {
    if (typeof jobId === 'undefined' || typeof currentTaskItemIndex === 'undefined') {
      return
    }

    void navigate({
      to: '/process-document/review/$jobId',
      params: { jobId },
      search: { taskId: documentOCRListData?.[currentTaskItemIndex - 1].taskId },
    })
  }

  const handleClickNextTaskButton = () => {
    if (typeof jobId === 'undefined' || typeof currentTaskItemIndex === 'undefined') {
      return
    }

    void navigate({
      to: '/process-document/review/$jobId',
      params: { jobId },
      search: { taskId: documentOCRListData?.[currentTaskItemIndex + 1].taskId },
    })
  }

  const handleClickSubmitButton = async () => {
    if (!ocrResults || Object.values(ocrResults).length === 0) {
      return
    }

    const targetOcrResults = Object.values(ocrResults).filter(({ taskId }) => taskIds?.includes(taskId))

    const payload: ReqSubmitOCRTasksMutation = {
      reviewData: targetOcrResults.map((ocrResult) => constructOCRDataPayload(ocrResult)),
    }

    await submitTasks(payload, {
      onSuccess: (response) => {
        const { archiveCount } = response
        void navigate({
          to: '/cases/active',
          search: {
            page: 1,
          },
        })
        toast.success(
          archiveCount === 0 ? (
            'Your documents are successfully uploaded!'
          ) : (
            <>
              Your documents are successfully uploaded! <br />
              {archiveCount === 1 ? '1 document is archived.' : `${archiveCount} documents are archived.`}
            </>
          )
        )
        clearDocumentOCRResultStore()
      },
      onError: () => {
        toast.error('Failed to upload documents.', {
          position: 'top-center',
        })
      },
    })
  }

  return {
    isOpenLeavePageConfirmDialog,
    isInReviewPage,
    isFirstTaskItem,
    isLastTaskItem,
    isAllTaskReviewed,
    isSubmittingOCRTasks,
    handleClickBackToHomeButton,
    handleChangeOpenLeavePageConfirmDialog,
    handleClickLeavePageConfirmDialogCancelButton,
    handleClickLeavePageConfirmDialogOkButton,
    handleClickPreviousTaskButton,
    handleClickNextTaskButton,
    handleClickSubmitButton,
  }
}
